import {useCallback} from 'react';

import type {OnShippingAddressChangeData} from '@paypal/paypal-js/types';

import {AxiosResponse} from 'axios';
import FormData from 'form-data';
import {useDispatch} from 'react-redux';

import {saveShippingMethodApi} from 'api/custom';
import {ENDPOINTS} from 'constants/endpoints';
import {makeFormAddressData} from 'desktop/pages/cart/utils/makeFormAddressData';
import {FIELDS} from 'desktop/pages/checkout/store/sagas/deliveryStep/saveCustomerAddressSaga/constants';
import {getShippingAddress} from 'desktop/pages/checkout/store/selectors/shippingAddress/shippingAddress';
import {AppThunkDispatchType} from 'desktop/store';
import {useHostSelectorRef} from 'hooks/useSelectorRef/useHostSelectorRef';
import {useSelectorRef} from 'hooks/useSelectorRef/useSelectorRef';
import {RegionsItemType} from 'hostApplicationStoreType';
import {apiConfig, HTTP_REQUEST_HEADER, HTTP_REQUEST_HEADER_CONTENT_TYPE_VALUE} from 'libs/api/api';
import {fetchStates} from 'reducers/location/location';
import {getCustomer} from 'selectors/customer';
import {ShippingMethodType} from 'types/shippingMethodsType';

import {OnShippingAddressChangeAdditionalType} from './types';

/**
 * Save partial address with mock data, save first available shipping method
 */
export const useSaveMockAddress = () => {
    const shippingFromDeliveryStepRef = useSelectorRef(getShippingAddress);
    const customerRef = useHostSelectorRef(getCustomer);
    const dispatch = useDispatch<AppThunkDispatchType>();

    const saveMockAddress = useCallback(
        async (addressFromPayPal: OnShippingAddressChangeData['shippingAddress']) => {
            const {city, countryCode, postalCode, state: payPalState} = addressFromPayPal!;

            const {
                payload: {data: states},
            } = (await dispatch(fetchStates(countryCode))) as unknown as {
                payload: {data: RegionsItemType[]};
            };

            const regionId = states?.find?.(
                state => state.value === payPalState || state.label === payPalState,
            )?.id;

            const newAddresses: Record<(typeof FIELDS)[number][1], string> = {
                address: 'StreetEmpty',
                addressID: '',
                city: city || 'CityEmpty',
                country: countryCode || 'CountryEmpty',
                email: customerRef.current?.email || 'empty@acme.com',
                firstName: 'FirstNameEmpty',
                lastName: 'LastNameEmpty',
                other: '',
                phone: 'PhoneEmpty',
                postal: postalCode || 'PostCodeEmpty',
                regionID: regionId || 'RegionIdEmpty',
                state: payPalState || 'RegionEmpty',
            };

            const data: FormData = makeFormAddressData({
                billing: newAddresses,
                shipping: newAddresses,
            });

            await apiConfig.instanceGusaBackend({
                data,
                headers: {
                    [HTTP_REQUEST_HEADER.CONTENT_TYPE]:
                        HTTP_REQUEST_HEADER_CONTENT_TYPE_VALUE.MULTIPART_FORM_DATA,
                },
                method: 'post',
                url: `${ENDPOINTS.SAVE_ADDRESS}`,
            });
        },
        [customerRef, dispatch],
    );

    const getShippingMethods = useCallback(async (countryCode: string, postalCode: string) => {
        const {data: availableShippings}: AxiosResponse<ShippingMethodType[]> =
            await apiConfig.instanceGusaBackend.get(
                `${ENDPOINTS.CHECKOUT_GET_SHIPPING}?${new URLSearchParams({
                    country: countryCode,
                    postcode: postalCode,
                }).toString()}`,
            );

        return availableShippings;
    }, []);

    const getAndSaveFirstShipping = useCallback(
        async (countryCode: string, postalCode: string) => {
            const availableShippings = await getShippingMethods(countryCode, postalCode);

            if (!availableShippings[0]) throw new Error('Shipping isn"t available');

            await saveShippingMethodApi(availableShippings[0].code);

            return availableShippings;
        },
        [getShippingMethods],
    );

    const saveAddressAndShipping = useCallback(
        async (
            addressFromPayPal: OnShippingAddressChangeData['shippingAddress'],
            {addressChangeCounter, placeOfInteraction}: OnShippingAddressChangeAdditionalType,
        ): Promise<ShippingMethodType[]> => {
            const {country, postal} = shippingFromDeliveryStepRef.current;
            const isSameAddressFromCheckout =
                country.value === addressFromPayPal!.countryCode &&
                postal.value === addressFromPayPal!.postalCode;
            const shouldSaveAddress =
                // address should be saved always in cart page
                placeOfInteraction === 'cart' ||
                // address should not be saved when paypal trigger it's change first time and it's the same as in checkout
                !(addressChangeCounter === 1 && isSameAddressFromCheckout);

            if (shouldSaveAddress) {
                await saveMockAddress(addressFromPayPal);
                const availableShippings = await getAndSaveFirstShipping(
                    addressFromPayPal!.countryCode,
                    addressFromPayPal!.postalCode,
                );

                // paypal require that one of the shipping should be selected, when address is changed we save first shipping
                // only first shipping should be selected
                return availableShippings.map((shipping, index) => ({
                    ...shipping,
                    selected: index === 0,
                }));
            }

            // if address should not be saved get shipping methods for further update
            return await getShippingMethods(
                addressFromPayPal.countryCode,
                addressFromPayPal.postalCode,
            );
        },
        [shippingFromDeliveryStepRef, getShippingMethods, saveMockAddress, getAndSaveFirstShipping],
    );

    return saveAddressAndShipping;
};
